/**
 * WebSocket Client
 *
 * Robust WebSocket client with automatic reconnection,
 * event handling, and channel subscriptions.
 */

// =============================================================================
// Types
// =============================================================================

export type EventType =
  // Connection
  | 'connected'
  | 'disconnected'
  | 'error'
  | 'ping'
  | 'pong'
  // Decisions
  | 'decision.created'
  | 'decision.updated'
  | 'decision.approved'
  | 'decision.rejected'
  | 'decision.executed'
  | 'decision.completed'
  // Capacity
  | 'capacity.updated'
  | 'capacity.alert'
  // Agents
  | 'agent.status_changed'
  | 'agent.prediction'
  // Games
  | 'game.started'
  | 'game.updated'
  | 'game.event'
  | 'game.ended'
  | 'tactical.recommendation'
  // Metrics
  | 'metrics.updated'
  // System
  | 'system.alert';

export interface WebSocketEvent<T = Record<string, unknown>> {
  type: EventType;
  timestamp: string;
  org_id?: string;
  correlation_id?: string;
  data: T;
}

export interface DecisionEventData {
  decision_id: string;
  title: string;
  agent: 'capacity' | 'revenue' | 'risk' | 'timing' | 'coordination';
  status: 'pending' | 'approved' | 'rejected' | 'executing' | 'completed';
  confidence: number;
  urgency: 'low' | 'medium' | 'high' | 'critical';
  impact: Record<string, number>;
  vertical: 'ops' | 'marketing' | 'sports';
}

export interface CapacityEventData {
  team_id?: string;
  team_name?: string;
  utilization: number;
  previous_utilization?: number;
  alert_level: 'normal' | 'warning' | 'critical';
}

export interface GameEventData {
  game_id: string;
  home_team: string;
  away_team: string;
  home_score: number;
  away_score: number;
  quarter: number;
  time_remaining: number;
  status: 'scheduled' | 'live' | 'halftime' | 'final';
  win_probability_home?: number;
  momentum_score?: number;
}

export interface TacticalRecommendationData {
  game_id: string;
  recommendation_id: string;
  type: 'substitution' | 'play_call' | 'defensive_adjustment' | 'timeout' | 'formation';
  priority: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  confidence: number;
  expires_at?: string;
}

export interface MetricsEventData {
  active_decisions: number;
  pending_approvals: number;
  team_capacity: number;
  revenue_impact: number;
  success_rate: number;
  changes: Record<string, number>;
}

export interface AlertEventData {
  alert_id: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  title: string;
  message: string;
  source: string;
  action_required: boolean;
  action_url?: string;
}

export type EventHandler<T = unknown> = (event: WebSocketEvent<T>) => void;

export interface WebSocketClientOptions {
  url: string;
  token?: string;
  reconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  pingInterval?: number;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

// =============================================================================
// WebSocket Client
// =============================================================================

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private options: Required<WebSocketClientOptions>;
  private handlers: Map<EventType | '*', Set<EventHandler>> = new Map();
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingTimeout: ReturnType<typeof setInterval> | null = null;
  private subscriptions: Set<string> = new Set();
  private isConnecting = false;

  constructor(options: WebSocketClientOptions) {
    this.options = {
      reconnect: true,
      reconnectInterval: 3000,
      maxReconnectAttempts: 10,
      pingInterval: 30000,
      onConnect: () => {},
      onDisconnect: () => {},
      onError: () => {},
      ...options,
    };
  }

  // ===========================================================================
  // Connection Management
  // ===========================================================================

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    const url = new URL(this.options.url);
    if (this.options.token) {
      url.searchParams.set('token', this.options.token);
    }

    try {
      this.ws = new WebSocket(url.toString());

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.options.onConnect();
        this.startPing();

        // Resubscribe to channels
        if (this.subscriptions.size > 0) {
          this.send({
            type: 'subscribe',
            channels: Array.from(this.subscriptions),
          });
        }
      };

      this.ws.onclose = () => {
        this.isConnecting = false;
        this.stopPing();
        this.options.onDisconnect();

        if (this.options.reconnect) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        this.isConnecting = false;
        this.options.onError(error);
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event);
      };
    } catch (error) {
      this.isConnecting = false;
      console.error('WebSocket connection error:', error);
    }
  }

  disconnect(): void {
    this.options.reconnect = false;
    this.stopPing();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      console.error('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.options.reconnectInterval * Math.min(this.reconnectAttempts, 5);

    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private startPing(): void {
    this.pingTimeout = setInterval(() => {
      this.send({ type: 'ping', data: { timestamp: new Date().toISOString() } });
    }, this.options.pingInterval);
  }

  private stopPing(): void {
    if (this.pingTimeout) {
      clearInterval(this.pingTimeout);
      this.pingTimeout = null;
    }
  }

  // ===========================================================================
  // Messaging
  // ===========================================================================

  send(message: Record<string, unknown>): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected');
      return false;
    }

    try {
      this.ws.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('Failed to send message:', error);
      return false;
    }
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data) as WebSocketEvent;

      // Handle pong internally
      if (data.type === 'pong') {
        return;
      }

      // Call specific handlers
      const handlers = this.handlers.get(data.type as EventType);
      if (handlers) {
        handlers.forEach((handler) => handler(data));
      }

      // Call wildcard handlers
      const wildcardHandlers = this.handlers.get('*');
      if (wildcardHandlers) {
        wildcardHandlers.forEach((handler) => handler(data));
      }
    } catch (error) {
      console.error('Failed to parse message:', error);
    }
  }

  // ===========================================================================
  // Subscriptions
  // ===========================================================================

  subscribe(channels: string | string[]): void {
    const channelList = Array.isArray(channels) ? channels : [channels];

    channelList.forEach((channel) => {
      this.subscriptions.add(channel);
    });

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send({ type: 'subscribe', channels: channelList });
    }
  }

  unsubscribe(channels: string | string[]): void {
    const channelList = Array.isArray(channels) ? channels : [channels];

    channelList.forEach((channel) => {
      this.subscriptions.delete(channel);
    });

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send({ type: 'unsubscribe', channels: channelList });
    }
  }

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  on<T = unknown>(eventType: EventType | '*', handler: EventHandler<T>): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }

    this.handlers.get(eventType)!.add(handler as EventHandler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(eventType)?.delete(handler as EventHandler);
    };
  }

  off(eventType: EventType | '*', handler?: EventHandler): void {
    if (handler) {
      this.handlers.get(eventType)?.delete(handler);
    } else {
      this.handlers.delete(eventType);
    }
  }

  // ===========================================================================
  // State
  // ===========================================================================

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  get subscribedChannels(): string[] {
    return Array.from(this.subscriptions);
  }
}

// =============================================================================
// Singleton Factory
// =============================================================================

let client: WebSocketClient | null = null;

export function getWebSocketClient(options?: Partial<WebSocketClientOptions>): WebSocketClient {
  if (!client) {
    const wsUrl = import.meta.env.VITE_WEBSOCKET_URL || 'ws://localhost:8005/api/v1/ws';
    client = new WebSocketClient({
      url: wsUrl,
      ...options,
    });
  }
  return client;
}

export function destroyWebSocketClient(): void {
  if (client) {
    client.disconnect();
    client = null;
  }
}
